

import React, { useState, useEffect } from "react";
import { DialogTitle, Typography } from "@mui/material";
import { toast } from "react-hot-toast";
import Select from "react-select";

import { axiosInstance } from "../../utility/apiInstance";
import { useRole } from "../MangeContextProvide";
import ModalPopup from "../../common/ModalPopup";
import AddcustomfieldsForm from "../AddcustomfieldsForm";
import AirShippingShowModal from "./ShowShipping";
import DialogBox from "../../page/Awbtrackedlist/DialogBox";
import MilestoneGroupingAccordionBody from "./MilestoneGroupingAccordion";
import AlertConfigAccordionBody, {
  AlertConfig,
  EMPTY_ALERT_CONFIG,
} from "./AlertConfigAccordionBody";

import "./AddTrackingDrawer.css";
import "./AlertConfigAccordionBody.css";
import { FieldData } from "./customFieltType";
import { convertArray } from "./customFieldtConvert";
import CustomFieldsPanel, { buildEmptyRow, FieldRowEntry } from "./CustomFieldsPanel";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AirLine {
  _id: string;
  airlineCode: string;
  airlineName: string;
  airlineTwolettercode: string;
}

export interface TrackingEntry {
  trackingNumber: string;
  customFields: FieldData[];
  customerName?: any;
  groupMilestone?: any;
  alertConfig?: AlertConfig;
}

export interface CfAccordionEntry {
  id: string;
  identifier: string;
  fieldName: string;
  fields: FieldData[];
  customerName?: any;
  groupMilestone?: any;
  alertConfig?: AlertConfig;
}

interface AirModalState {
  subcriptionPopup: boolean;
  mileStonePopup: boolean;
  groupmilestoneModal: boolean;
  customfieldpopup: boolean;
  trackingCfPopup: boolean;
  cfTabPopup: boolean;
  perEntryGroupingPopup: boolean;
  perEntryAlertPopup: boolean;
}

type InputMode = "tracking" | "customFields";

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildFieldstype(allField: any[]) {
  return allField.map((item, index) => {
    let f_type: "Textbox" | "select" | "Date" = "Textbox";
    if (item.fieldType === "Dropdown List" || item.fieldType === "Yes/No")
      f_type = "select";
    if (item.fieldType === "Date") f_type = "Date";
    const options =
      item.fieldType === "Dropdown List"
        ? item.dropdownOptions.map((o: string, i: number) => ({
          id: i + 1,
          label: o,
        }))
        : item.fieldType === "Yes/No"
          ? [
            { id: 1, label: "Yes" },
            { id: 2, label: "No" },
          ]
          : [];
    return {
      id: index + 1,
      f_name: item.fieldName,
      f_type,
      default_value: "",
      is_required: item.required ? 1 : 0,
      options,
    };
  });
}

export function buildBlankFieldSet(
  allField: any[],
  uniqueSetKey: number | string
): FieldData[] {
  return allField.map((item: any, i: number) => ({
    id: `${uniqueSetKey}_${i + 1}`,
    autocompleteValue: {
      id: i + 1,
      label: item.fieldName,
      required: item.required,
      customFieldId: item._id,
      isUnique: item.isUnique,
    },
    textValue: "",
    label: item.fieldName,
    value: "",
    required: item.required,
    customFieldId: item._id,
    isUnique: item.isUnique,
  }));
}

function reconcileEntries(
  newAwbList: string[],
  prevEntries: TrackingEntry[],
  allField: any[]
): TrackingEntry[] {
  const existingMap = new Map(prevEntries.map((e) => [e.trackingNumber, e]));
  return newAwbList.map((awb, idx) => {
    if (existingMap.has(awb)) return existingMap.get(awb)!;
    const sameIndexEntry = prevEntries[idx];
    if (sameIndexEntry) {
      return { ...sameIndexEntry, trackingNumber: awb };
    }
    return {
      trackingNumber: awb,
      customFields: buildBlankFieldSet(allField, `${awb}_${idx}_${Date.now()}`),
    };
  });
}

function applyPrefill(
  blankFields: FieldData[],
  prefillSet: FieldData[],
  forceUpdate = false
): FieldData[] {
  if (!prefillSet || prefillSet.length === 0) return blankFields;

  const prefillMap = new Map<string, FieldData["value"]>(
    prefillSet
      .filter(
        (f) =>
          f.customFieldId != null &&
          f.customFieldId !== "" &&
          f.value !== "" &&
          f.value !== undefined
      )
      .map((f) => [f.customFieldId as string, f.value])
  );

  if (prefillMap.size === 0) return blankFields;

  return blankFields.map((field) => {
    if (!forceUpdate && field.value !== "" && field.value !== undefined)
      return field;
    if (!field.customFieldId) return field;
    const prefilled = prefillMap.get(field.customFieldId);
    if (prefilled !== undefined) {
      return {
        ...field,
        value: prefilled,
        textValue: Array.isArray(prefilled) ? "" : (prefilled as string),
      };
    }
    return field;
  });
}

type UniqueFilledField = FieldData & {
  value: string;
  label: string;
  customFieldId: string;
};

function buildCfAccordionEntries(
  fieldSets: FieldData[][],
  allField: any[],
  prevEntries: CfAccordionEntry[]
): CfAccordionEntry[] {
  const prevMap = new Map(prevEntries.map((e) => [e.id, e]));

  const uniqueFilledFields: UniqueFilledField[] = [];

  for (const set of fieldSets) {
    for (const field of set) {
      if (
        field.isUnique === true &&
        typeof field.value === "string" &&
        field.value.trim() !== "" &&
        typeof field.label === "string" &&
        field.label !== "" &&
        typeof field.customFieldId === "string" &&
        field.customFieldId !== ""
      ) {
        uniqueFilledFields.push(field as UniqueFilledField);
      }
    }
  }

  return uniqueFilledFields.map((uniqueField) => {
    const entryId = `cfacc_${uniqueField.id}`;
    const existing = prevMap.get(entryId);

    if (existing) {
      return {
        ...existing,
        // ✅ Always update identifier to the latest typed value
        identifier: uniqueField.value,
        fieldName: uniqueField.label,
        // ✅ Update the prefilled field with the new value,
        //    preserve any edits user made to other fields
        fields: existing.fields.map((f) =>
          f.customFieldId === uniqueField.customFieldId
            ? {
              ...f,
              value: uniqueField.value,
              textValue: uniqueField.value,
              prefilled: true,
            }
            : f
        ),
      };
    }

    const blankFields = buildBlankFieldSet(allField, `${entryId}_${Date.now()}`);
    const markedFields = blankFields.map((f) =>
      f.customFieldId === uniqueField.customFieldId
        ? { ...f, prefilled: true }
        : f
    );
    return {
      id: entryId,
      identifier: uniqueField.value,
      fieldName: uniqueField.label,
      fields: applyPrefill(markedFields, [uniqueField]),
    };
  });
}

const emptySubscriptionForm = {
  whatsApp: [],
  emails: [],
  whatsAppmilestones: [],
  emailsmilestones: [],
  mileStoneType: "",
  whatsAppgroups: [],
  whatsappGroupmilestones: [],
  groupMilestone: { _id: "" },
  flags: [],
  customerName: null,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AddTrackingDrawer({ setShowAirBulk, activeTab, drawerClose }: any) {
  const { chargeRate, availableCredit } = useRole() as any;

  const [allField, setAllField] = useState<any[]>([]);

  // ── Tracking tab state ────────────────────────────────────────────────────
  const [awbs, setAwbs] = useState("");
  const [trackingEntries, setTrackingEntries] = useState<TrackingEntry[]>([]);
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [entryFieldErrors, setEntryFieldErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [entryErrors, setEntryErrors] = useState<
    Record<number, Record<string, { autocomplete: boolean; text: boolean }>>
  >({});

  // ── Custom fields tab state — panel ───────────────────────────────────────
  const [fieldSets, setFieldSets] = useState<FieldData[][]>([]);
  const [errors, setErrors] = useState<
    Record<number, Record<string, { autocomplete: boolean; text: boolean }>>
  >({});
  const [fieldErrors, setFieldErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [dataArray, setDataArray] = useState<FieldData[]>([]);
  const [setCounter, setSetCounter] = useState(1);

  // ── Lifted CustomFieldsPanel row state ───────────────────────────────────
  const [cfRows, setCfRows] = useState<FieldRowEntry[]>([
    buildEmptyRow(`row_${Date.now()}`),
  ]);
  const [cfUniqueErrors, setCfUniqueErrors] = useState<Record<string, string>>({});
  const [cfValidating, setCfValidating] = useState<Record<string, boolean>>({});

  // ── Custom fields tab — accordion popup state ─────────────────────────────
  const [cfAccordionEntries, setCfAccordionEntries] = useState<CfAccordionEntry[]>([]);
  const [cfOpenAccordion, setCfOpenAccordion] = useState<number | null>(0);
  const [cfEntryFieldErrors, setCfEntryFieldErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [cfEntryErrors, setCfEntryErrors] = useState<
    Record<number, Record<string, { autocomplete: boolean; text: boolean }>>
  >({});

  // ── Per-entry milestone grouping state ────────────────────────────────────
  const [applyAllMilestone, setApplyAllMilestone] = useState<any>(null);
  const [groupingOpenAccordion, setGroupingOpenAccordion] = useState<number | null>(0);

  // ── Per-entry alert config state ──────────────────────────────────────────
  const [applyAllAlert, setApplyAllAlert] = useState<AlertConfig | null>(null);
  const [alertOpenAccordion, setAlertOpenAccordion] = useState<number | null>(0);
  const [sharedContactList, setSharedContactList] = useState<any[]>([]);
  const [sharedTemplateList, setSharedTemplateList] = useState<any[]>([]);
  const [sharedOrgList, setSharedOrgList] = useState<any[]>([]);

  // ── Shared state ──────────────────────────────────────────────────────────
  const [airLines, setAirLines] = useState<AirLine[]>([]);
  const [airLinesModal, setAirLinesModal] = useState(false);
  const [airShow, setAirShow] = useState(false);
  const [canClosedrawer, setCanCloseDrawer] = useState(false);
  const [airErrorList, setAirErrorList] = useState<any>(null);
  const [airStatusMsg, setAirStatusMsg] = useState<any>({});
  const [airSubForm, setAirSubForm] = useState<any>({ ...emptySubscriptionForm });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [airModal, setAirModal] = useState<AirModalState>({
    subcriptionPopup: false,
    mileStonePopup: false,
    groupmilestoneModal: false,
    customfieldpopup: false,
    trackingCfPopup: false,
    cfTabPopup: false,
    perEntryGroupingPopup: false,
    perEntryAlertPopup: false,
  });

  const [inputMode, setInputMode] = useState<InputMode>("tracking");

  const isLowAir = availableCredit <= 0 || availableCredit < chargeRate?.awbRate;
  const Fieldstype = buildFieldstype(allField);
  const simpleOptions = allField.map((item, i) => ({
    id: i + 1,
    label: item.fieldName,
    required: item.required,
  }));

  // ── Derived: hasUnique (needed before the effect below) ──────────────────
  const hasUnique = fieldSets.some((set) =>
    set.some((f) => f.isUnique && f.value !== "" && f.value !== undefined)
  );

  // ── KEY FIX: Re-sync cfAccordionEntries whenever fieldSets changes ────────
  // This ensures that edits made in CustomFieldsPanel (changing the unique
  // field value) are immediately reflected in cfAccordionEntries, so that
  // trackingEntries in the payload always has the latest value.
  useEffect(() => {
    if (inputMode !== "customFields") return;

    if (!hasUnique) {
      setCfAccordionEntries([]);
      return;
    }

    setCfAccordionEntries((prev) =>
      buildCfAccordionEntries(fieldSets, allField, prev)
    );
  }, [fieldSets, inputMode]);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const getCustomForms = async () => {
    try {
      const { data } = await axiosInstance.get("/api/v1/custom-form/admin-fields");
      const requiredFields = data.data.filter((ele: any) => ele.required);
      setAllField(requiredFields);
      setFieldSets([buildBlankFieldSet(requiredFields, 0)]);
      setSetCounter(1);
    } catch (_) { }
  };

  const fetchAirData = async () => {
    try {
      const [msRes, wgRes, flRes, alRes, gmRes] = await Promise.all([
        axiosInstance.get("/api/v1/mileStone"),
        axiosInstance.get("/api/v1/whatsapp-group"),
        axiosInstance.get("/api/v1/flags"),
        axiosInstance.get("api/v1/allowed-airline"),
        axiosInstance.get("/api/v1/groupingMileStone?type=air"),
      ]);
      setAirSubForm((p: any) => ({
        ...p,
        milestoneList: msRes.data,
        whatsappGroupList: wgRes.data?.data,
        flagsList: flRes.data?.data || [],
        groupingmilestonesList: gmRes.data?.data || [],
      }));
      if (alRes.status === 200 && alRes.data.success) setAirLines(alRes.data.data);
    } catch (_) { }
  };

  const fetchSharedContacts = async () => {
    try {
      const [contactRes, tplRes, orgRes] = await Promise.all([
        axiosInstance.get("/api/v1/contact"),
        axiosInstance.get("/api/v1/subscription-template"),
        axiosInstance.get("/api/v1/master-contact-organization"),
      ]);
      setSharedContactList(contactRes.data?.data || []);
      setSharedTemplateList(tplRes.data?.data || []);
      setSharedOrgList(orgRes.data?.data || []);
    } catch (_) {
      setSharedContactList([]);
      setSharedTemplateList([]);
      setSharedOrgList([]);
    }
  };

  useEffect(() => {
    getCustomForms();
    fetchAirData();
    fetchSharedContacts();
  }, []);

  // ── Tab switch ────────────────────────────────────────────────────────────

  const handleModeSwitch = (mode: InputMode) => {
    if (mode === inputMode) return;
    setAwbs("");
    setTrackingEntries([]);
    setEntryFieldErrors({});
    setEntryErrors({});
    setOpenAccordion(0);
    setCfAccordionEntries([]);
    setCfOpenAccordion(0);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setErrors({});
    setFieldErrors({});
    setAirSubForm((prev: any) => ({ ...prev, ...emptySubscriptionForm }));
    setCfRows([buildEmptyRow(`row_${Date.now()}`)]);
    setCfUniqueErrors({});
    setCfValidating({});
    setFieldSets([buildBlankFieldSet(allField, Date.now())]);
    setSetCounter((c) => c + 1);
    setApplyAllMilestone(null);
    setApplyAllAlert(null);
    setInputMode(mode);
  };

  // ── Tracking tab handlers ─────────────────────────────────────────────────

  const handleAirInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as InputEvent;
    const inputValue = e.target.value;

    let nextAwbs: string;

    if (
      nativeEvent.inputType === "deleteContentBackward" ||
      nativeEvent.inputType === "deleteByCut"
    ) {
      nextAwbs = inputValue.trim() === "" ? "" : awbs.slice(0, -1);
    } else if (inputValue.trim() === "") {
      nextAwbs = "";
    } else {
      let fmt = inputValue
        .replace(/[\r\n]+/g, ",")
        .replace(/\s*,\s*/g, ",")
        .replace(/[^0-9,]/g, "")
        .trim();
      fmt = fmt
        .split(",")
        .filter((a) => a.trim() !== ",")
        .map((awb) => {
          if (nativeEvent.inputType !== "insertText") {
            if (awb.length === 9) awb = "00" + awb;
            else if (awb.length === 10) awb = "0" + awb;
          }
          if (awb.length > 3 && awb[3] !== "-")
            awb = awb.slice(0, 3) + "-" + awb.slice(3);
          return awb;
        })
        .join(",");
      nextAwbs = fmt;
    }

    setAwbs(nextAwbs);

    const awbList = nextAwbs
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);

    if (awbList.length === 0) {
      setTrackingEntries([]);
    } else {
      setTrackingEntries((prev) => {
        const reconciled = reconcileEntries(awbList, prev, allField);
        const prevMap = new Map(prev.map((e) => [e.trackingNumber, e]));
        return reconciled.map((entry) => {
          if (prevMap.has(entry.trackingNumber)) return entry;
          return {
            ...entry,
            customFields: applyPrefill(entry.customFields, fieldSets[0] ?? []),
          };
        });
      });
    }

    setEntryFieldErrors({});
    setEntryErrors({});
  };

  const makeSetCustomerName = (entryIdx: number) => (option: any) => {
    setTrackingEntries((prev) =>
      prev.map((entry, i) => {
        if (i === entryIdx) return { ...entry, customerName: option };
        if (entryIdx === 0 && !entry.customerName)
          return { ...entry, customerName: option };
        return entry;
      })
    );
  };

  const makeCfSetCustomerName = (entryIdx: number) => (option: any) => {
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) => {
        if (i === entryIdx) return { ...entry, customerName: option };
        if (entryIdx === 0 && !entry.customerName)
          return { ...entry, customerName: option };
        return entry;
      })
    );
  };

  const makeSetFields =
    (entryIdx: number) =>
      (updater: React.SetStateAction<FieldData[]>) => {
        setTrackingEntries((prev) =>
          prev.map((entry, i) => {
            if (i !== entryIdx) return entry;
            const next =
              typeof updater === "function" ? updater(entry.customFields) : updater;
            return { ...entry, customFields: next };
          })
        );
      };

  const makeSetEntryFieldErrors =
    (entryIdx: number) =>
      (updater: React.SetStateAction<Record<string, string>>) => {
        setEntryFieldErrors((prev) => {
          const current = prev[entryIdx] ?? {};
          const next =
            typeof updater === "function" ? updater(current) : updater;
          return { ...prev, [entryIdx]: next };
        });
      };

  const makeSetEntryErrors =
    (entryIdx: number) => (updater: React.SetStateAction<any>) => {
      setEntryErrors((prev) => {
        const current = prev[entryIdx] ?? {};
        const next =
          typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [entryIdx]: next };
      });
    };

  const validateEntries = (): boolean => {
    const newEntryFieldErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    let firstErrorIdx: number | null = null;

    trackingEntries.forEach((entry, entryIdx) => {
      entry.customFields.forEach((field) => {
        if (!field.required) return;
        const isEmpty =
          field.value === "" ||
          field.value === undefined ||
          (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newEntryFieldErrors[entryIdx]) newEntryFieldErrors[entryIdx] = {};
          newEntryFieldErrors[entryIdx][field.id] = `${field.label} is required`;
          hasError = true;
          if (firstErrorIdx === null) firstErrorIdx = entryIdx;
        }
      });
    });

    if (hasError) {
      setEntryFieldErrors(newEntryFieldErrors);
      if (firstErrorIdx !== null) setOpenAccordion(firstErrorIdx);
      return false;
    }
    return true;
  };

  const openTrackingCfPopup = () => {
    if (!awbs.trim()) {
      toast.error("Please enter at least one AWB number first.");
      return;
    }
    if (fieldSets[0] && fieldSets[0].length > 0) {
      setTrackingEntries((prev) =>
        prev.map((entry) => ({
          ...entry,
          customFields: applyPrefill(entry.customFields, fieldSets[0]),
        }))
      );
    }
    setOpenAccordion(0);
    setAirModal((p) => ({ ...p, trackingCfPopup: true }));
  };

  const handleTrackingCfOk = () => {
    if (!validateEntries()) return;
    setAirModal((p) => ({ ...p, trackingCfPopup: false }));
  };

  // ── Per-entry milestone grouping handlers ─────────────────────────────────

  const makeSetGroupMilestone = (entryIdx: number) => (option: any) => {
    setApplyAllMilestone(null);
    setTrackingEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIdx ? { ...entry, groupMilestone: option } : entry
      )
    );
  };

  const makeCfSetGroupMilestone = (entryIdx: number) => (option: any) => {
    setApplyAllMilestone(null);
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIdx ? { ...entry, groupMilestone: option } : entry
      )
    );
  };

  const handleApplyAllMilestone = (option: any) => {
    setApplyAllMilestone(option);
    if (inputMode === "tracking") {
      setTrackingEntries((prev) =>
        prev.map((entry) => ({ ...entry, groupMilestone: option }))
      );
    } else {
      setCfAccordionEntries((prev) =>
        prev.map((entry) => ({ ...entry, groupMilestone: option }))
      );
    }
  };

  const openPerEntryGroupingPopup = () => {
    if (inputMode === "tracking") {
      if (!awbs.trim()) {
        toast.error("Please enter at least one AWB number first.");
        return;
      }
    } else {
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field first.");
        return;
      }
      // cfAccordionEntries is already up-to-date via the useEffect sync,
      // but we still call buildCfAccordionEntries here to catch any entries
      // that were cleared (no longer have a unique value) and remove them.
      const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);
      setCfAccordionEntries(entries);
    }
    setApplyAllMilestone(null);
    setGroupingOpenAccordion(0);
    setAirModal((p) => ({ ...p, perEntryGroupingPopup: true }));
  };

  // ── Per-entry alert config handlers ───────────────────────────────────────

  const makeSetAlertConfig = (entryIdx: number) => (cfg: AlertConfig) => {
    setApplyAllAlert(null);
    setTrackingEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIdx ? { ...entry, alertConfig: cfg } : entry
      )
    );
  };

  const makeCfSetAlertConfig = (entryIdx: number) => (cfg: AlertConfig) => {
    setApplyAllAlert(null);
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIdx ? { ...entry, alertConfig: cfg } : entry
      )
    );
  };

  const handleApplyAllAlert = (cfg: AlertConfig) => {
    setApplyAllAlert(cfg);
    if (inputMode === "tracking") {
      setTrackingEntries((prev) =>
        prev.map((entry) => ({ ...entry, alertConfig: cfg }))
      );
    } else {
      setCfAccordionEntries((prev) =>
        prev.map((entry) => ({ ...entry, alertConfig: cfg }))
      );
    }
  };

  const openPerEntryAlertPopup = () => {
    if (inputMode === "tracking") {
      if (!awbs.trim()) {
        toast.error("Please enter at least one AWB number first.");
        return;
      }
    } else {
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field first.");
        return;
      }
      // cfAccordionEntries is already up-to-date via the useEffect sync,
      // but we still call buildCfAccordionEntries here to catch any entries
      // that were cleared and remove them.
      const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);
      setCfAccordionEntries(entries);
    }
    setApplyAllAlert(null);
    setAlertOpenAccordion(0);
    setAirModal((p) => ({ ...p, perEntryAlertPopup: true }));
  };

  // ── Custom fields tab handlers ────────────────────────────────────────────

  const makeCfSetFields =
    (entryIdx: number) =>
      (updater: React.SetStateAction<FieldData[]>) => {
        setCfAccordionEntries((prev) =>
          prev.map((entry, i) => {
            if (i !== entryIdx) return entry;
            const next =
              typeof updater === "function" ? updater(entry.fields) : updater;
            return { ...entry, fields: next };
          })
        );
      };

  const makeCfEntryFieldErrors =
    (entryIdx: number) =>
      (updater: React.SetStateAction<Record<string, string>>) => {
        setCfEntryFieldErrors((prev) => {
          const current = prev[entryIdx] ?? {};
          const next =
            typeof updater === "function" ? updater(current) : updater;
          return { ...prev, [entryIdx]: next };
        });
      };

  const makeCfEntryErrors =
    (entryIdx: number) => (updater: React.SetStateAction<any>) => {
      setCfEntryErrors((prev) => {
        const current = prev[entryIdx] ?? {};
        const next =
          typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [entryIdx]: next };
      });
    };

  const validateCfEntries = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    let firstErrorIdx: number | null = null;

    cfAccordionEntries.forEach((entry, entryIdx) => {
      entry.fields.forEach((field) => {
        if (!field.required) return;
        const isEmpty =
          field.value === "" ||
          field.value === undefined ||
          (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newErrors[entryIdx]) newErrors[entryIdx] = {};
          newErrors[entryIdx][field.id] = `${field.label} is required`;
          hasError = true;
          if (firstErrorIdx === null) firstErrorIdx = entryIdx;
        }
      });
    });

    if (hasError) {
      setCfEntryFieldErrors(newErrors);
      if (firstErrorIdx !== null) setCfOpenAccordion(firstErrorIdx);
      return false;
    }
    return true;
  };

  const openCfTabPopup = () => {
    const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);

    if (entries.length === 0) {
      toast.error("Please fill at least one Unique custom field first.");
      return;
    }

    setCfAccordionEntries(entries);
    setCfOpenAccordion(0);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setAirModal((p) => ({ ...p, cfTabPopup: true }));
  };

  const handleCfTabOk = () => {
    if (!validateCfEntries()) return;
    setAirModal((p) => ({ ...p, cfTabPopup: false }));
  };

  const transformAlertConfigData = (data: any) => {
    return {
      whatsApp: (data?.whatsApp || []).map((item: { value: string }) => item.value) || [],
      emails: (data?.emails || []).map((item: { value: string }) => item.value) || [],
      whatsAppgroups: (data?.whatsAppgroups || []).map((item: { value: string }) => item.value) || [],
      whatsAppmilestones: data?.whatsAppmilestones || [],
      emailsmilestones: data?.emailsmilestones || [],
      whatsappGroupmilestones: data?.whatsappGroupmilestones || [],
    };
  };

  // ── Payload builder ───────────────────────────────────────────────────────

  const buildAirPayload = (
    awbsValue: string[] | null,
    customfieldsValue: any[],
    entries: TrackingEntry[]
  ) => {
    return {
      awbs: awbsValue,
      whatApps: airSubForm.whatsApp?.map((i: any) => i.value),
      emails: airSubForm.emails?.map((i: any) => i.value),
      whatsAppmilestones: airSubForm.whatsAppmilestones,
      emailsmilestones: airSubForm.emailsmilestones,
      whatsappGroups: airSubForm.whatsAppgroups?.map((i: any) => i.value),
      whatsappGroupMilestones: airSubForm.whatsappGroupmilestones,
      tag: null,
      groupMilestone: airSubForm.groupMilestone?.value || null,
      flags: airSubForm.flags?.map((i: any) => i.value),
      contactOrganization: airSubForm.contactOrganization?.value || "",
      customfields: customfieldsValue,
      customerName: null,
      trackingEntries: entries.map((entry) => ({
        ...entry,
        customFields: convertArray(entry.customFields),
        customerName:
          entry.customerName?.item?.name || entry.customerName?.label || null,
        groupMilestone: entry.groupMilestone?.value || null,
        alertConfig: transformAlertConfigData(entry.alertConfig ?? null),
      })),
    };
  };

  const resetAll = () => {
    setAirSubForm((prev: any) => ({ ...prev, ...emptySubscriptionForm }));
    setAwbs("");
    setTrackingEntries([]);
    setEntryFieldErrors({});
    setEntryErrors({});
    const key = setCounter;
    setFieldSets([buildBlankFieldSet(allField, key)]);
    setSetCounter((c) => c + 1);
    setErrors({});
    setFieldErrors({});
    setCfAccordionEntries([]);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setCfRows([buildEmptyRow(`row_${Date.now()}`)]);
    setCfUniqueErrors({});
    setCfValidating({});
    setApplyAllMilestone(null);
    setApplyAllAlert(null);
  };

  const validateAllSets = (): boolean => {
    const newFieldErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    fieldSets.forEach((set, posIdx) => {
      set.forEach((field) => {
        if (!field.required) return;
        const isEmpty =
          field.value === "" ||
          field.value === undefined ||
          (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newFieldErrors[posIdx]) newFieldErrors[posIdx] = {};
          newFieldErrors[posIdx][field.id] = `${field.label} is required`;
          hasError = true;
        }
      });
    });
    if (hasError) {
      setFieldErrors(newFieldErrors);
      return false;
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleAirSubscribe = async () => {
    setCanCloseDrawer(false);
    if (inputMode === "tracking") {
      if (!awbs.trim()) {
        toast.error("Please enter at least one AWB number.");
        return;
      }
      const awbList = awbs
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean);
      const customfieldsValue = trackingEntries.map((e) =>
        convertArray(e.customFields)
      );
      try {
        setIsSubmitting(true);
        const payload = buildAirPayload(awbList, customfieldsValue, trackingEntries);
        const res = await axiosInstance.post(
          "/api/v1/airwaybill/add-airwaybill",
          payload
        );
        setAirStatusMsg(res.data?.message);
        if (res.data.success) {
          setAirErrorList(
            res.data.data.failed.length > 0 ? res.data.data.failed : null
          );
          setAirShow(true);
          if (res.data.data.failed.length === 0) {
            resetAll();
            setCanCloseDrawer(true);
          }
        }
      } catch (err: any) {
        const errData = err?.response?.data;
        if (errData?.error?.length > 0) {
          const logs = Array.isArray(errData.error)
            ? errData.error.map((e: any) => ({ awb: "", reason: e.message }))
            : [{ awb: "", reason: errData.error }];
          setAirErrorList(logs);
          setAirStatusMsg({});
          setAirShow(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!validateAllSets()) {
        toast.error("No entries found. Please fill a Unique field first.");
        return;
      }
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field.");
        return;
      }

      // ✅ Use cfAccordionEntries directly — they are already kept in sync
      // by the useEffect above, so no need to rebuild here.
      const cfTrackingEntries: TrackingEntry[] = cfAccordionEntries.map((entry) => ({
        trackingNumber: entry.identifier,
        customFields: entry.fields,
        customerName: entry.customerName,
        groupMilestone: entry.groupMilestone,
        alertConfig: entry.alertConfig,
      }));

      try {
        setIsSubmitting(true);
        const payload = buildAirPayload(
          null,
          fieldSets.map((set) => convertArray(set)),
          cfTrackingEntries
        );
        const res = await axiosInstance.post(
          "/api/v1/airwaybill/add-airwaybill",
          payload
        );
        setAirStatusMsg(res.data?.message);
        if (res.data.success) {
          setAirErrorList(
            res.data.data.failed.length > 0 ? res.data.data.failed : null
          );
          setAirShow(true);
          if (res.data.data.failed.length === 0) {
            resetAll();
            setCanCloseDrawer(true);
          }
        }
      } catch (err: any) {
        const errData = err?.response?.data;
        if (errData?.error?.length > 0) {
          const logs = Array.isArray(errData.error)
            ? errData.error.map((e: any) => ({ awb: "", reason: e.message }))
            : [{ awb: "", reason: errData.error }];
          setAirErrorList(logs);
          setAirStatusMsg({});
          setAirShow(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // ── Derived UI values ─────────────────────────────────────────────────────

  const airConfigured =
    airSubForm.whatsAppmilestones?.length > 0 ||
    airSubForm.emailsmilestones?.length > 0;

  const filledEntriesCount = trackingEntries.filter((e) =>
    e.customFields.some(
      (f) =>
        f.value !== "" &&
        f.value !== undefined &&
        !(Array.isArray(f.value) && f.value.length === 0)
    )
  ).length;
  const awbCount = trackingEntries.length;

  const cfCount = fieldSets.filter(
    (set) => set[0]?.isUnique && (set[0]?.value ?? "") !== ""
  ).length;

  const cfConfiguredCount = cfAccordionEntries.filter((entry) =>
    entry.fields.some(
      (f) =>
        !f.prefilled &&
        f.value !== "" &&
        f.value !== undefined &&
        !(Array.isArray(f.value) && f.value.length === 0)
    )
  ).length;

  const trackingGroupingConfigured = trackingEntries.some((e) => e.groupMilestone);
  const cfGroupingConfigured = cfAccordionEntries.some((e) => e.groupMilestone);

  const milestoneOptions =
    airSubForm?.groupingmilestonesList?.map((item: any) => ({
      label: item?.groupName,
      value: item?._id,
      item,
    })) ?? [];

  // Alert config badge helpers
  const isAlertConfigured = (cfg?: AlertConfig) =>
    !!cfg &&
    (cfg.whatsApp.length > 0 ||
      cfg.emails.length > 0 ||
      cfg.whatsAppgroups.length > 0);

  const trackingAlertConfigured = trackingEntries.some((e) =>
    isAlertConfigured(e.alertConfig)
  );
  const cfAlertConfigured = cfAccordionEntries.some((e) =>
    isAlertConfigured(e.alertConfig)
  );

  const trackingAlertCount = trackingEntries.filter((e) =>
    isAlertConfigured(e.alertConfig)
  ).length;
  const cfAlertCount = cfAccordionEntries.filter((e) =>
    isAlertConfigured(e.alertConfig)
  ).length;

  // Shared milestone list for AlertConfigAccordionBody
  const sharedMilestoneList = airSubForm.milestoneList ?? [];

  const handleAlertConfig = () => {
    if (inputMode === "tracking") {
      if (!awbs.trim()) {
        toast.error("Please enter at least one AWB number.");
        return;
      }
    } else {
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field first.");
        return;
      }
    }
    setAirModal((p) => ({ ...p, subcriptionPopup: true }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className={`atd-panel${activeTab === "air" ? " active" : ""}`}>
        {isLowAir && (
          <div className="atd-low-balance">
            ⚠️ &nbsp;Your account balance is low. Please top up to add new
            tracking numbers.
          </div>
        )}

        <div className="atd-body-top-main">
          <div className="atd-body-top">

            {/* ── Tab Toggle ── */}
            <div className="atd-mode-toggle">
              <button
                className={`atd-mode-btn${inputMode === "tracking" ? " atd-mode-btn--active" : ""}`}
                onClick={() => handleModeSwitch("tracking")}
              >
                <span className="atd-mode-icon">🔢</span>
                Tracking Number
              </button>
              <button
                className={`atd-mode-btn${inputMode === "customFields" ? " atd-mode-btn--active" : ""}`}
                onClick={() => handleModeSwitch("customFields")}
              >
                <span className="atd-mode-icon">📋</span>
                Custom Fields
              </button>
            </div>

            {/* ════════ TAB: TRACKING NUMBER ════════ */}
            {inputMode === "tracking" && (
              <>
                <div className="atd-section-label">Tracking Number</div>
                <textarea
                  className="atd-textarea"
                  placeholder={
                    "Add AWB(s)\ne.g. 123-45678901\nSeparate multiple with commas"
                  }
                  value={awbs}
                  onChange={handleAirInput}
                  disabled={isLowAir}
                />
                <div className="atd-hint-row">
                  <span className="atd-format-hint">Format: XXX-XXXXXXXX</span>
                  <button
                    className="atd-airlines-link"
                    onClick={() => setAirLinesModal(true)}
                  >
                    Available airlines ↗
                  </button>
                </div>
              </>
            )}

            {/* ════════ TAB: CUSTOM FIELDS ════════ */}
            {inputMode === "customFields" && (
              <CustomFieldsPanel
                allField={allField}
                fieldSets={fieldSets}
                setFieldSets={setFieldSets}
                setCounter={setCounter}
                setSetCounter={setSetCounter}
                simpleOptions={simpleOptions}
                Fieldstype={Fieldstype}
                errors={errors}
                setErrors={setErrors}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                setDataArray={setDataArray}
                airSubForm={airSubForm}
                setAirSubForm={setAirSubForm}
                airModal={airModal}
                setAirModal={setAirModal}
                buildBlankFieldSet={buildBlankFieldSet}
                rows={cfRows}
                setRows={setCfRows}
                uniqueErrors={cfUniqueErrors}
                setUniqueErrors={setCfUniqueErrors}
                validating={cfValidating}
                setValidating={setCfValidating}
              />
            )}

            {/* ── Configuration (always visible) ── */}
            <div className="atd-section-label">Configuration</div>
            <div className="atd-config-section">

              {/* ── Alert Triggers — per-entry popup ── */}
              <div
                className="atd-config-row"
                onClick={openPerEntryAlertPopup}
              >
                <div className="atd-config-left">
                  <div className="atd-config-icon">🔔</div>
                  <div>
                    <div className="atd-config-text">Alert Triggers</div>

                    {inputMode === "tracking" && trackingEntries.length > 0 ? (
                      <div className={trackingAlertConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {trackingAlertConfigured ? "✓ " : ""}
                        {trackingAlertCount} of {awbCount} AWB
                        {awbCount !== 1 ? "s" : ""} configured
                      </div>
                    ) : inputMode === "customFields" && cfCount > 0 ? (
                      <div className={cfAlertConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {cfAlertConfigured ? "✓ " : ""}
                        {cfAlertCount} of {cfCount} entr
                        {cfCount !== 1 ? "ies" : "y"} configured
                      </div>
                    ) : airConfigured ? (
                      <div className="atd-badge">
                        ✓ {airSubForm.whatsAppmilestones.length} WA ·{" "}
                        {airSubForm.emailsmilestones.length} Email
                      </div>
                    ) : (
                      <div className="atd-config-sub">
                        WhatsApp &amp; Email milestones
                      </div>
                    )}
                  </div>
                </div>
                <span className="atd-config-chevron">›</span>
              </div>

              {/* ── Milestone Grouping ── */}
              <div
                className="atd-config-row"
                onClick={openPerEntryGroupingPopup}
              >
                <div className="atd-config-left">
                  <div className="atd-config-icon">🏁</div>
                  <div>
                    <div className="atd-config-text">Milestone Grouping</div>

                    {inputMode === "tracking" && trackingEntries.length > 0 ? (
                      <div className={trackingGroupingConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {trackingGroupingConfigured ? "✓ " : ""}
                        {trackingEntries.filter((e) => e.groupMilestone).length}{" "}
                        of {trackingEntries.length} AWB
                        {trackingEntries.length !== 1 ? "s" : ""} configured
                      </div>
                    ) : inputMode === "customFields" && cfCount > 0 ? (
                      <div className={cfGroupingConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {cfGroupingConfigured ? "✓ " : ""}
                        {cfAccordionEntries.filter((e) => e.groupMilestone).length}{" "}
                        of {cfCount} entr
                        {cfCount !== 1 ? "ies" : "y"} configured
                      </div>
                    ) : airSubForm.groupMilestone?.value ? (
                      <div className="atd-badge">
                        ✓ {airSubForm.groupMilestone?.item?.groupName}
                      </div>
                    ) : (
                      <div className="atd-config-sub">
                        Assign a grouping template
                      </div>
                    )}
                  </div>
                </div>
                <span className="atd-config-chevron">›</span>
              </div>

              {/* ── Tracking tab: custom fields config row ── */}
              {inputMode === "tracking" && (
                <div className="atd-config-row" onClick={openTrackingCfPopup}>
                  <div className="atd-config-left">
                    <div className="atd-config-icon">📋</div>
                    <div>
                      <div className="atd-config-text">Custom Fields</div>
                      {awbCount > 0 ? (
                        <div className="atd-badge">
                          ✓ {filledEntriesCount} of {awbCount} AWB
                          {awbCount !== 1 ? "s" : ""} configured
                        </div>
                      ) : (
                        <div className="atd-config-sub">
                          Add custom field values per AWB
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="atd-config-chevron">›</span>
                </div>
              )}

              {/* ── Custom Fields tab: its own config row ── */}
              {inputMode === "customFields" && (
                <div className="atd-config-row" onClick={openCfTabPopup}>
                  <div className="atd-config-left">
                    <div className="atd-config-icon">📋</div>
                    <div>
                      <div className="atd-config-text">Custom Fields</div>
                      {cfCount > 0 ? (
                        <div className="atd-badge">
                          ✓ {cfConfiguredCount} of {cfCount} entr
                          {cfCount !== 1 ? "ies" : "y"} configured
                        </div>
                      ) : (
                        <div className="atd-config-sub">
                          Review &amp; fill remaining fields per entry
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="atd-config-chevron">›</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="footerDrawerTracking">
            <button
              className="btn btnYellow"
              onClick={handleAirSubscribe}
              disabled={isLowAir || isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Tracking"}
            </button>
            <div className="atd-csv-row" onClick={() => setShowAirBulk(true)}>
              <div className="atd-csv-icon">↑</div>
              <div className="atd-csv-text-wrap">
                <div className="atd-csv-title">Bulk CSV Upload</div>
                <div className="atd-csv-desc">
                  Add multiple AWBs with custom rules
                </div>
              </div>
              <span className="atd-csv-chevron">›</span>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          SHARED MODALS
      ════════════════════════════════════════════════════════════════════ */}

      <AirShippingShowModal
        open={airLinesModal}
        handleClose={() => setAirLinesModal(false)}
        airLines={airLines}
      />

      <DialogBox
        open={airShow}
        successMessage={
          <DialogTitle>
            <Typography
              variant="h6"
              className={
                airStatusMsg?.messageStatus === 1
                  ? "text-green-500"
                  : "text-red-500"
              }
            >
              {airStatusMsg?.message}
            </Typography>
          </DialogTitle>
        }
        handleClose={() => {
          setAirShow(false);
          setAirErrorList(null);
          canClosedrawer && drawerClose();
        }}
        data={airErrorList}
      />

      {/* ════════════════════════════════════════════════════════════════════
          TRACKING TAB — Custom Fields popup
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={airModal.trackingCfPopup}
        handleClose={() => {
          setAirModal((p) => ({ ...p, trackingCfPopup: false }));
          setEntryFieldErrors({});
        }}
        heading={"Custom Fields"}
      >
        {airModal.trackingCfPopup && (
          <div className="atd-cf-modal-body">
            {trackingEntries.length === 0 ? (
              <div className="atd-cf-empty">
                <span>📋</span>
                <p>No AWBs found. Please enter tracking numbers first.</p>
              </div>
            ) : (
              trackingEntries.map((entry, entryIdx) => {
                const isOpen = openAccordion === entryIdx;
                const hasFilledFields =
                  convertArray(entry.customFields).length > 0;
                const hasErrors =
                  !!entryFieldErrors[entryIdx] &&
                  Object.keys(entryFieldErrors[entryIdx]).length > 0;

                return (
                  <div
                    key={entry.trackingNumber}
                    className={[
                      "atd-cf-accordion",
                      isOpen ? "atd-cf-accordion--open" : "",
                      hasErrors ? "atd-cf-accordion--error" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    <button
                      type="button"
                      className="atd-cf-accordion-header"
                      onClick={() =>
                        setOpenAccordion(isOpen ? null : entryIdx)
                      }
                    >
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">
                          {entryIdx + 1}
                        </span>
                        <span className="atd-cf-accordion-awb">
                          {entry.trackingNumber}
                        </span>
                        {hasErrors && (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--error">
                            ⚠ Required fields missing
                          </span>
                        )}
                        {!hasErrors && hasFilledFields && (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                            ✓ Filled
                          </span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">
                        {isOpen ? "▲" : "▼"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AddcustomfieldsForm
                          setmodalPopup={setAirModal}
                          fields={entry.customFields}
                          setFields={makeSetFields(entryIdx)}
                          errors={entryErrors[entryIdx] ?? {}}
                          setErrors={makeSetEntryErrors(entryIdx)}
                          setDataArray={setDataArray}
                          simpleOptions={simpleOptions}
                          Fieldstype={Fieldstype}
                          fieldErrors={entryFieldErrors[entryIdx] ?? {}}
                          setFieldErrors={makeSetEntryFieldErrors(entryIdx)}
                          subscriptionFormData={airSubForm}
                          setSubscriptionFormData={setAirSubForm}
                          entryCustomerName={entry.customerName ?? null}
                          onCustomerNameChange={makeSetCustomerName(entryIdx)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div
              className="modalInnerFieldFooter flex justify-end"
              style={{ marginTop: 16 }}
            >
              <button
                className="btnTheme btnPadding"
                onClick={handleTrackingCfOk}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          CUSTOM FIELDS TAB — accordion popup
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={airModal.cfTabPopup}
        handleClose={() => {
          setAirModal((p) => ({ ...p, cfTabPopup: false }));
          setCfEntryFieldErrors({});
        }}
        heading={"Custom Fields"}
      >
        {airModal.cfTabPopup && (
          <div className="atd-cf-modal-body">
            {cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty">
                <span>📋</span>
                <p>
                  No entries found. Please fill a Unique field in the panel
                  first.
                </p>
              </div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = cfOpenAccordion === entryIdx;
                const hasFilledFields = convertArray(entry.fields).length > 0;
                const hasErrors =
                  !!cfEntryFieldErrors[entryIdx] &&
                  Object.keys(cfEntryFieldErrors[entryIdx]).length > 0;

                return (
                  <div
                    key={entry.id}
                    className={[
                      "atd-cf-accordion",
                      isOpen ? "atd-cf-accordion--open" : "",
                      hasErrors ? "atd-cf-accordion--error" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    <button
                      type="button"
                      className="atd-cf-accordion-header"
                      onClick={() =>
                        setCfOpenAccordion(isOpen ? null : entryIdx)
                      }
                    >
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">
                          {entryIdx + 1}
                        </span>
                        <span className="atd-cf-accordion-awb">
                          {entry.identifier}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#888",
                            marginLeft: 4,
                            fontWeight: 400,
                          }}
                        >
                          {entry.fieldName}
                        </span>
                        {hasErrors && (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--error">
                            ⚠ Required fields missing
                          </span>
                        )}
                        {!hasErrors && hasFilledFields && (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                            ✓ Filled
                          </span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">
                        {isOpen ? "▲" : "▼"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AddcustomfieldsForm
                          setmodalPopup={setAirModal}
                          fields={entry.fields}
                          setFields={makeCfSetFields(entryIdx)}
                          errors={cfEntryErrors[entryIdx] ?? {}}
                          setErrors={makeCfEntryErrors(entryIdx)}
                          setDataArray={setDataArray}
                          simpleOptions={simpleOptions}
                          Fieldstype={Fieldstype}
                          fieldErrors={cfEntryFieldErrors[entryIdx] ?? {}}
                          setFieldErrors={makeCfEntryFieldErrors(entryIdx)}
                          subscriptionFormData={airSubForm}
                          setSubscriptionFormData={setAirSubForm}
                          entryCustomerName={entry.customerName ?? null}
                          onCustomerNameChange={makeCfSetCustomerName(entryIdx)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div
              className="modalInnerFieldFooter flex justify-end"
              style={{ marginTop: 16 }}
            >
              <button
                className="btnTheme btnPadding"
                onClick={handleCfTabOk}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          PER-ENTRY MILESTONE GROUPING POPUP
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={airModal.perEntryGroupingPopup}
        handleClose={() =>
          setAirModal((p) => ({ ...p, perEntryGroupingPopup: false }))
        }
        heading={"Milestone Grouping"}
      >
        {airModal.perEntryGroupingPopup && (
          <div className="atd-cf-modal-body">

            {/* ── Apply to All banner ── */}
            <div
              style={{
                background: "#f8f8f8",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>🏁</span> Apply to All
              </div>
              <Select
                isClearable
                placeholder="Select milestone grouping for all entries..."
                options={milestoneOptions}
                value={applyAllMilestone}
                onChange={(option) => handleApplyAllMilestone(option)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#fff",
                    borderColor: "#ddd",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#bbb" },
                  }),
                  placeholder: (base) => ({ ...base, color: "#999" }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {applyAllMilestone && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#666",
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ color: "#22c55e" }}>✓</span>
                  Applied to all entries. You can still override individually
                  below — doing so will clear the &quot;Apply to All&quot;
                  selection.
                </div>
              )}
            </div>

            {/* ── Per-entry accordions ── */}
            {inputMode === "tracking" ? (
              trackingEntries.length === 0 ? (
                <div className="atd-cf-empty">
                  <span>🏁</span>
                  <p>No AWBs found. Please enter tracking numbers first.</p>
                </div>
              ) : (
                trackingEntries.map((entry, entryIdx) => {
                  const isOpen = groupingOpenAccordion === entryIdx;
                  const milestone = entry.groupMilestone;

                  return (
                    <div
                      key={entry.trackingNumber}
                      className={[
                        "atd-cf-accordion",
                        isOpen ? "atd-cf-accordion--open" : "",
                      ]
                        .join(" ")
                        .trim()}
                    >
                      <button
                        type="button"
                        className="atd-cf-accordion-header"
                        onClick={() =>
                          setGroupingOpenAccordion(isOpen ? null : entryIdx)
                        }
                      >
                        <div className="atd-cf-accordion-left">
                          <span className="atd-cf-accordion-num">
                            {entryIdx + 1}
                          </span>
                          <span className="atd-cf-accordion-awb">
                            {entry.trackingNumber}
                          </span>
                          {milestone ? (
                            <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                              ✓ {milestone.item?.groupName ?? milestone.label}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 11,
                                color: "#aaa",
                                marginLeft: 6,
                              }}
                            >
                              Not set
                            </span>
                          )}
                        </div>
                        <span className="atd-cf-accordion-chevron">
                          {isOpen ? "▲" : "▼"}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="atd-cf-accordion-body">
                          <MilestoneGroupingAccordionBody
                            milestoneOptions={milestoneOptions}
                            value={entry.groupMilestone ?? null}
                            onChange={makeSetGroupMilestone(entryIdx)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty">
                <span>🏁</span>
                <p>
                  No entries found. Please fill a Unique field in the panel
                  first.
                </p>
              </div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = groupingOpenAccordion === entryIdx;
                const milestone = entry.groupMilestone;

                return (
                  <div
                    key={entry.id}
                    className={[
                      "atd-cf-accordion",
                      isOpen ? "atd-cf-accordion--open" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    <button
                      type="button"
                      className="atd-cf-accordion-header"
                      onClick={() =>
                        setGroupingOpenAccordion(isOpen ? null : entryIdx)
                      }
                    >
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">
                          {entryIdx + 1}
                        </span>
                        <span className="atd-cf-accordion-awb">
                          {entry.identifier}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#888",
                            marginLeft: 4,
                            fontWeight: 400,
                          }}
                        >
                          {entry.fieldName}
                        </span>
                        {milestone ? (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                            ✓ {milestone.item?.groupName ?? milestone.label}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#aaa",
                              marginLeft: 6,
                            }}
                          >
                            Not set
                          </span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">
                        {isOpen ? "▲" : "▼"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <MilestoneGroupingAccordionBody
                          milestoneOptions={milestoneOptions}
                          value={entry.groupMilestone ?? null}
                          onChange={makeCfSetGroupMilestone(entryIdx)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div
              className="modalInnerFieldFooter flex justify-end"
              style={{ marginTop: 16 }}
            >
              <button
                className="btnTheme btnPadding"
                onClick={() =>
                  setAirModal((p) => ({ ...p, perEntryGroupingPopup: false }))
                }
              >
                OK
              </button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          PER-ENTRY ALERT TRIGGERS POPUP
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={airModal.perEntryAlertPopup}
        handleClose={() =>
          setAirModal((p) => ({ ...p, perEntryAlertPopup: false }))
        }
        heading={"Alert Triggers"}
      >
        {airModal.perEntryAlertPopup && (
          <div className="atd-cf-modal-body">

            {/* ── Apply to All banner ── */}
            <div
              style={{
                background: "#f8f8f8",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 10,
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>🔔</span> Apply to All Entries
              </div>
              <AlertConfigAccordionBody
                value={applyAllAlert ?? EMPTY_ALERT_CONFIG}
                onChange={(cfg) => handleApplyAllAlert(cfg)}
                allContactList={sharedContactList}
                templateList={sharedTemplateList}
                organisationList={sharedOrgList}
                whatsappGroupList={airSubForm.whatsappGroupList ?? []}
                milestoneList={sharedMilestoneList}
              />
              {applyAllAlert && isAlertConfigured(applyAllAlert) && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#666",
                    marginTop: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ color: "#22c55e" }}>✓</span>
                  Applied to all entries. You can still override individually
                  below — doing so will clear the &quot;Apply to All&quot;
                  selection.
                </div>
              )}
            </div>

            {/* ── Per-entry accordions ── */}
            {inputMode === "tracking" ? (
              trackingEntries.length === 0 ? (
                <div className="atd-cf-empty">
                  <span>🔔</span>
                  <p>No AWBs found. Please enter tracking numbers first.</p>
                </div>
              ) : (
                trackingEntries.map((entry, entryIdx) => {
                  const isOpen = alertOpenAccordion === entryIdx;
                  const cfg = entry.alertConfig;
                  const hasAny = isAlertConfigured(cfg);

                  return (
                    <div
                      key={entry.trackingNumber}
                      className={[
                        "atd-cf-accordion",
                        isOpen ? "atd-cf-accordion--open" : "",
                      ]
                        .join(" ")
                        .trim()}
                    >
                      <button
                        type="button"
                        className="atd-cf-accordion-header"
                        onClick={() =>
                          setAlertOpenAccordion(isOpen ? null : entryIdx)
                        }
                      >
                        <div className="atd-cf-accordion-left">
                          <span className="atd-cf-accordion-num">
                            {entryIdx + 1}
                          </span>
                          <span className="atd-cf-accordion-awb">
                            {entry.trackingNumber}
                          </span>
                          {hasAny ? (
                            <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                              ✓
                              {cfg!.whatsApp.length > 0
                                ? ` ${cfg!.whatsApp.length} WA`
                                : ""}
                              {cfg!.emails.length > 0
                                ? ` · ${cfg!.emails.length} Email`
                                : ""}
                              {cfg!.whatsAppgroups.length > 0
                                ? ` · ${cfg!.whatsAppgroups.length} Group`
                                : ""}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 11,
                                color: "#aaa",
                                marginLeft: 6,
                              }}
                            >
                              Not configured
                            </span>
                          )}
                        </div>
                        <span className="atd-cf-accordion-chevron">
                          {isOpen ? "▲" : "▼"}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="atd-cf-accordion-body">
                          <AlertConfigAccordionBody
                            value={entry.alertConfig ?? EMPTY_ALERT_CONFIG}
                            onChange={makeSetAlertConfig(entryIdx)}
                            allContactList={sharedContactList}
                            templateList={sharedTemplateList}
                            organisationList={sharedOrgList}
                            whatsappGroupList={airSubForm.whatsappGroupList ?? []}
                            milestoneList={sharedMilestoneList}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty">
                <span>🔔</span>
                <p>
                  No entries found. Please fill a Unique field in the panel
                  first.
                </p>
              </div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = alertOpenAccordion === entryIdx;
                const cfg = entry.alertConfig;
                const hasAny = isAlertConfigured(cfg);

                return (
                  <div
                    key={entry.id}
                    className={[
                      "atd-cf-accordion",
                      isOpen ? "atd-cf-accordion--open" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    <button
                      type="button"
                      className="atd-cf-accordion-header"
                      onClick={() =>
                        setAlertOpenAccordion(isOpen ? null : entryIdx)
                      }
                    >
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">
                          {entryIdx + 1}
                        </span>
                        <span className="atd-cf-accordion-awb">
                          {entry.identifier}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#888",
                            marginLeft: 4,
                            fontWeight: 400,
                          }}
                        >
                          {entry.fieldName}
                        </span>
                        {hasAny ? (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                            ✓
                            {cfg!.whatsApp.length > 0
                              ? ` ${cfg!.whatsApp.length} WA`
                              : ""}
                            {cfg!.emails.length > 0
                              ? ` · ${cfg!.emails.length} Email`
                              : ""}
                            {cfg!.whatsAppgroups.length > 0
                              ? ` · ${cfg!.whatsAppgroups.length} Group`
                              : ""}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#aaa",
                              marginLeft: 6,
                            }}
                          >
                            Not configured
                          </span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">
                        {isOpen ? "▲" : "▼"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AlertConfigAccordionBody
                          value={entry.alertConfig ?? EMPTY_ALERT_CONFIG}
                          onChange={makeCfSetAlertConfig(entryIdx)}
                          allContactList={sharedContactList}
                          templateList={sharedTemplateList}
                          organisationList={sharedOrgList}
                          whatsappGroupList={airSubForm.whatsappGroupList ?? []}
                          milestoneList={sharedMilestoneList}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div
              className="modalInnerFieldFooter flex justify-end"
              style={{ marginTop: 16 }}
            >
              <button
                className="btnTheme btnPadding"
                onClick={() =>
                  setAirModal((p) => ({ ...p, perEntryAlertPopup: false }))
                }
              >
                OK
              </button>
            </div>
          </div>
        )}
      </ModalPopup>
    </>
  );
}